<script setup lang="ts">
    import type { MolHeaderDesktopNav } from '#build/components';
    import type { DestopMenuItemProps, HandleMenuKeypress } from '~/components/atm/DesktopMenuItem.vue';
    import type { CallToAction, MenuItem } from '~/graphql/generated';

    const nuxtApp = useNuxtApp();
    const appStore = useAppStore();
    const { showBikefinderButton, bikefinderLocaleUrl, handleBikefinder, isBikefinder } = useBikefinder();
    const { accountEnabled } = useAccount();

    const showFlyout = ref(false);
    const showMobileMenu = ref(false);
    const mobileLevelTitles = ref<string[]>([]);
    const mobileLevelTitle = computed(() => {
        return mobileLevelTitles.value.findLast(Boolean) || '';
    });

    const MAX_SUBMENUS = 3;
    type SubMenu = {
        items: MenuItem[];
        cta?: CallToAction | null;
        ctavisibility?: string | null;
    };
    const subMenus = ref<SubMenu[]>([]);
    const activeMenuLevelIds = ref<string[]>([]);
    const featuredDesktop = ref<MenuItem>();

    const headerElement = useTemplateRef<HTMLElement>('headerElement');
    const { randomHash } = useUtils();

    const { menuItems } = storeToRefs(appStore);

    // UTIL functions
    const getFocusable = (parent: HTMLElement | undefined | null, position: 'first' | 'last' = 'first') =>
        (position === 'first' ? parent?.querySelector('a, button') : Array.from(parent?.querySelectorAll('a, button') ?? []).pop()) as
            | HTMLAnchorElement
            | HTMLButtonElement
            | null;

    const getItem = (id: string) => {
        return appStore.getMenuItemById(id);
    };
    const getItems = (ids: string[] | undefined | null): MenuItem[] => {
        return (ids || []).map(getItem).filter(isDefined);
    };

    const closeMenu = () => {
        closeMobileMenu();
        closeFlyout();
        featuredDesktop.value = undefined;
    };

    const showLevel = ({ id, title = '', level = 0 }: { id: string; title?: string; level?: number }) => {
        if (subMenus.value.length >= MAX_SUBMENUS && level > MAX_SUBMENUS + 1) return;

        const nextItem = getItem(id);
        if (!nextItem) return;

        mobileLevelTitles.value.push(title);
        featuredDesktop.value = undefined;
        const nextMenu: SubMenu = {
            items: getItems(nextItem.items),
            cta: nextItem.cta,
            ctavisibility: nextItem.ctavisibility,
        };

        if (level) {
            const index = level - 1;
            subMenus.value = subMenus.value.slice(0, index);
            subMenus.value[index] = nextMenu;
        } else subMenus.value.push(nextMenu);

        activeMenuLevelIds.value.push(textToId(nextItem.title) || randomHash('alpha'));
    };

    useOnNavigation(closeMenu);

    // Mobile functions
    const toggleMobileMenu = (open: boolean) => {
        if (!open) return closeMobileMenu();
        showMobileMenu.value = true;
    };

    const closeMobileMenu = () => {
        showMobileMenu.value = false;
        mobileLevelTitles.value = [];
        subMenus.value = [];
    };

    const backMobileMenu = () => {
        mobileLevelTitles.value.pop();
        subMenus.value.pop();
    };

    const openMobileLevel = ({ id, title }: MenuItem) => {
        showLevel({ id, title });
    };

    // Desktop functions
    const openFlyout = (id: string) => {
        const m = getItem(id);
        if (!m?.items?.length) return closeFlyout();
        showFlyout.value = true;
        showLevel({ id, level: 1 });
    };
    const closeFlyout = () => {
        showFlyout.value = false;
        featuredDesktop.value = undefined;
        subMenus.value = [];
        activeMenuLevelIds.value = [];
    };

    const getSubMenuByIndex = (index: number) => {
        return subMenus.value[index] || { items: [] };
    };

    // A11y functions
    type HandleMenuKeypressParamsType = Parameters<HandleMenuKeypress>[number];

    const handleMenuTab = ({ isOpen, event, level, submenuId }: HandleMenuKeypressParamsType) => {
        if (isOpen && submenuId) {
            const menuItemToFocus = getFocusable(headerElement.value?.querySelector(`#${submenuId}-submenu`));
            menuItemToFocus?.focus();
            event.preventDefault();
        }
        // # focusTrap
        if (level === 1) return;
        const menuButton = event.target as HTMLElement;
        const parentUl = menuButton.closest('ul');
        if (!parentUl) return;

        const firstItem = getFocusable(parentUl);
        const lastItem = getFocusable(parentUl, 'last');
        if ((firstItem === menuButton && event.shiftKey) || (lastItem === menuButton && !event.shiftKey)) {
            event.shiftKey ? lastItem?.focus() : firstItem?.focus();
            event.preventDefault();
        }
    };
    const handleMenuEnterSpace = ({ isOpen, level, itemId }: HandleMenuKeypressParamsType) => {
        if (!itemId) return;
        if (level === 1) {
            isOpen ? openFlyout(itemId) : closeFlyout();
        } else if (isOpen) {
            showLevel({ id: itemId, level });
        } else {
            subMenus.value.pop();
        }
    };
    /**
     * @param level should be 2, 3 or 4
     */
    const focusLastOpenMenu = (level: number) => {
        const lastId = activeMenuLevelIds.value[level - 2];
        const toFocus = headerElement.value?.querySelector(`#${lastId}`) as HTMLElement | null;
        toFocus?.focus();
    };
    const handleMenuEsc = ({ level }: HandleMenuKeypressParamsType) => {
        if (level < 2) return;
        focusLastOpenMenu(level);
        subMenus.value.pop();
        if (level === 2) return closeFlyout();
    };
    const handleMenuKeypress = (handleMenuKeypressParams: HandleMenuKeypressParamsType) => {
        const key = handleMenuKeypressParams.event.code;
        if (key === 'Tab') return handleMenuTab(handleMenuKeypressParams);
        if (['Enter', 'NumpadEnter', 'Space'].includes(key)) return handleMenuEnterSpace(handleMenuKeypressParams);
        if (key === 'Escape') return handleMenuEsc(handleMenuKeypressParams);
    };
    const handleDetails = async (item: MenuItem) => {
        await nextTick();
        featuredDesktop.value = item;
    };

    onMounted(() => {
        nuxtApp.$scroll.disable(showMobileMenu);
    });
</script>

<template>
    <!-- mobile nav bar + logo -->
    <div class="relative z-lg flex w-full justify-center bg-white lg:hidden">
        <nuxt-link
            :aria-label="$t('error.homepage')"
            :to="$helpers.generateLocalePath('/')"
            class="header-logo my-0.5 flex fill-woom-red p-4">
            <woom-icon-woom-logo class="h-5" />
        </nuxt-link>
    </div>

    <mol-header-mobile-nav-bar
        class="lg:hidden"
        @toggle="toggleMobileMenu" />

    <teleport
        to="#vue-root-header"
        :disabled="!showMobileMenu">
        <!-- mobile level 1 -->
        <transition
            enter-from-class="opacity-0"
            enter-active-class="duration-200 ease-in"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-active-class="duration-200 ease-in"
            leave-to-class="opacity-0">
            <atm-header-mobile-nav-wrapper
                v-if="showMobileMenu"
                :menu="{ items: menuItems }"
                :menu-level="1"
                :menu-level-title="mobileLevelTitle"
                @back="backMobileMenu"
                @open="openMobileLevel">
                <atm-additional-link-button id="navigation_additional_link" />
                <atm-contact-button id="navigation_contact" />
                <atm-dealer-button id="navigation_dealer" />
                <atm-newsletter-button id="navigation_newsletter" />
                <atm-language-button id="navigation_language" />
            </atm-header-mobile-nav-wrapper>
        </transition>
        <!-- mobile sub menus -->
        <template v-for="(_, index) in Array(MAX_SUBMENUS)">
            <transition
                enter-from-class="translate-x-full"
                enter-active-class="duration-300 ease-in"
                enter-to-class="translate-x-0"
                leave-from-class="translate-x-0"
                leave-active-class="duration-300 ease-in"
                leave-to-class="translate-x-full">
                <atm-header-mobile-nav-wrapper
                    v-if="subMenus[index]"
                    :menu="subMenus[index]"
                    :menu-level="index + 2"
                    :menu-level-title="mobileLevelTitle"
                    @back="backMobileMenu"
                    @open="openMobileLevel" />
            </transition>
        </template>
    </teleport>

    <!-- desktop nav -->
    <header
        ref="headerElement"
        class="org-header sticky top-0 z-md flex flex-col drop-shadow max-lg:hidden">
        <!-- desktop top nav bar with icons -->
        <!-- z-index to fix drop shadow when search is open -->
        <div class="relative z-20 hidden bg-woom-white-smoke lg:block">
            <atm-container
                class="flex h-12 items-center justify-between"
                width="full">
                <atm-container
                    class="header-bar-search"
                    no-margin>
                    <mol-algolia-autocomplete display="desktop" />
                </atm-container>
                <div class="header-bar-items flex shrink-0 items-center gap-1 text-sm">
                    <atm-additional-link-button id="navigation_additional_link_desktop" />
                    <atm-contact-button id="navigation_contact_desktop" />
                    <atm-dealer-button id="navigation_dealer_desktop" />
                    <atm-newsletter-button id="navigation_newsletter_desktop" />
                    <div class="h-[20px] w-[1px] bg-gray-300"></div>
                    <atm-helpscout-button id="navigation_helpscout_desktop" />
                    <atm-language-button
                        theme="navbar"
                        id="navigation_language_desktop" />
                    <lazy-atm-account-button
                        v-if="accountEnabled"
                        id="navigation_account_desktop" />
                    <atm-cart-button id="navigation_cart_desktop" />
                </div>
            </atm-container>
            <!-- 
                This is only used for representational purposes
                Algolia Autocomplete does not have a close button on desktop
             -->
            <div
                class="header-bar-close-button absolute right-2 top-0 hidden h-12 w-12 items-center justify-center self-center"
                aria-hidden>
                <woom-icon-close class="w-6" />
            </div>
        </div>
        <div @mouseleave="closeFlyout">
            <div class="relative z-10 hidden bg-white drop-shadow lg:block">
                <atm-grid full-width>
                    <div class="col-span-full mx-auto flex h-16 w-full items-center justify-between gap-6">
                        <nuxt-link
                            :to="$helpers.generateLocalePath('/')"
                            :aria-label="$t('error.homepage')"
                            class="-ml-2 flex shrink-0 p-2">
                            <woom-icon-woom-logo class="h-5 fill-woom-red" />
                        </nuxt-link>

                        <nav class="flex-auto shrink-0">
                            <ul
                                class="flex list-none items-center justify-center gap-2"
                                role="menubar">
                                <li v-for="item in menuItems">
                                    <atm-desktop-menu-item
                                        :menu-level="1"
                                        v-if="item"
                                        :class="[
                                            'p-1 text-lg font-bold',
                                            { 'text-woom-red': item.items?.length && activeMenuLevelIds[0] === item.id },
                                        ]"
                                        :item="item"
                                        :handle-menu-keypress="handleMenuKeypress"
                                        v-bind="getMenuItemProps(item)"
                                        @mouseover="() => openFlyout(item.id)" />
                                </li>
                            </ul>
                        </nav>
                        <lazy-atm-button
                            v-if="showBikefinderButton"
                            :to="bikefinderLocaleUrl"
                            :aria-current="isBikefinder ? 'page' : undefined"
                            @click="handleBikefinder"
                            size="small"
                            kind="link"
                            class="overflow-hidden text-ellipsis"
                            skip-local-path>
                            {{ $t('bikefinder.label') }}
                        </lazy-atm-button>
                    </div>
                </atm-grid>
            </div>

            <transition
                enter-from-class="opacity-0"
                enter-active-class="duration-300 ease-in"
                enter-to-class="opacity-100"
                leave-from-class="opacity-100"
                leave-active-class="duration-300 ease-in"
                leave-to-class="opacity-0">
                <div v-show="showFlyout">
                    <div class="absolute flex max-h-screen-desktop min-h-[60vh] w-full justify-start bg-white">
                        <atm-grid class="w-full">
                            <template v-for="(_, index) in Array(MAX_SUBMENUS)">
                                <mol-header-desktop-nav
                                    v-if="getSubMenuByIndex(index).items.length"
                                    :menu="getSubMenuByIndex(index)"
                                    :menu-level="index + 2"
                                    :submenu-id="activeMenuLevelIds[index]"
                                    :handle-menu-keypress="handleMenuKeypress"
                                    :handle-mouseover="(id) => showLevel({ id, level: index + 2 })"
                                    :handle-details="handleDetails" />
                            </template>
                            <lazy-org-menu-feature
                                v-if="featuredDesktop"
                                :featured="featuredDesktop"
                                :class="[subMenus[2]?.items?.length ? 'max-xl:hidden xl:col-span-3' : 'col-span-4 xl:col-span-6']" />
                        </atm-grid>
                    </div>
                </div>
            </transition>
        </div>
    </header>
    <!-- desktop nav overlay -->
    <transition
        enter-from-class="opacity-0"
        enter-active-class="duration-300 ease-in"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-active-class="duration-300 ease-in"
        leave-to-class="opacity-0">
        <lazy-atm-overlay
            v-show="showFlyout"
            class="z-sm" />
    </transition>
</template>
